import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Sparkles, CheckCircle2 } from "lucide-react";
import { formatCOP } from "../../lib/apiClient";
import { tiers } from "./data";

/**
 * TiersSection — Niveles (pricing cards) — N0 a N2 con variantes auto/reviewed.
 */
export function TiersSection({ tierCtaHref }) {
  return (
    <section id="niveles" className="border-b border-gray-200 bg-brand-cream-soft" data-testid="landing-tiers-section">
      <div className="container-prose py-20">
        <div className="max-w-2xl mb-12">
          <p className="text-xs uppercase tracking-[0.22em] text-brand-coral font-semibold mb-3">Niveles</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-gray-900 mb-3">
            Empieza con prevalidación. Escala cuando el caso lo amerite.
          </h2>
          <p className="text-gray-600">
            Cinco niveles de profundidad. Variantes <strong>automatizadas</strong> (rápidas, más
            económicas) o con <strong>revisión humana experta</strong> (mayor seguridad).
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tiers.map((t) => (
            <TierCard key={t.key} tier={t} tierCtaHref={tierCtaHref} />
          ))}
        </div>

        <p className="text-xs text-gray-500 mt-8 max-w-2xl">
          Las variantes <strong>automatizadas</strong> entregan el documento sin revisión humana
          con aviso expreso. Las variantes <strong>Revisadas</strong> requieren aprobación de un
          revisor experto antes de habilitar la descarga.
        </p>
      </div>
    </section>
  );
}

function TierCard({ tier: t, tierCtaHref }) {
  const Icon = t.icon;
  return (
    <div
      data-testid={`tier-card-${t.key}`}
      className={`tier-card border bg-white p-6 flex flex-col rounded-sm relative ${
        t.highlight ? "tier-card-highlight" : "border-gray-200"
      }`}
    >
      {t.highlight && (
        <span className="absolute -top-3 left-6 inline-flex items-center gap-1 bg-brand-coral text-white text-[10px] uppercase tracking-widest px-3 py-1 rounded-sm font-semibold shadow-sm">
          <Sparkles className="h-3 w-3" aria-hidden="true" /> Más popular
        </span>
      )}

      <div className="mb-4">
        <div className="flex items-start justify-between mb-3">
          <div className="inline-flex items-center justify-center w-11 h-11 bg-brand-cream rounded-sm">
            <Icon className="h-5 w-5 text-brand-burgundy" aria-hidden="true" />
          </div>
        </div>
        <div className="font-serif text-xl text-gray-900 mb-1">{t.title}</div>
        <p className="text-xs text-gray-600 mb-3">{t.subtitle}</p>
        {t.badge && (
          <span
            className={`inline-block text-[9px] uppercase tracking-wider border px-2 py-[3px] rounded-sm font-semibold ${
              t.badge.color === "emerald"
                ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                : "bg-amber-50 text-amber-800 border-amber-300"
            }`}
          >
            {t.badge.label}
          </span>
        )}
      </div>

      <div className="mb-4 pb-4 border-b border-gray-200">
        <div className="font-serif text-3xl text-brand-burgundy font-semibold">
          {t.price === 0 ? "Gratis" : formatCOP(t.price)}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {t.price === 0 ? "Sin costo · Sin tarjeta" : "Pago único por caso · COP"}
        </p>
      </div>

      <p className="text-sm text-gray-700 mb-4 leading-relaxed">{t.description}</p>

      <ul className="space-y-2 text-sm text-gray-700 mb-6 flex-1">
        {t.features.map((f) => (
          <li key={f} className="flex gap-2 items-start">
            <CheckCircle2 className="h-4 w-4 text-brand-coral mt-[2px] shrink-0" aria-hidden="true" />
            <span className="text-xs leading-relaxed">{f}</span>
          </li>
        ))}
      </ul>

      <Button
        asChild
        className={`rounded-sm w-full ${
          t.highlight
            ? "bg-brand-burgundy hover:bg-brand-burgundy-light text-white shadow-md"
            : "bg-white border border-gray-300 text-brand-burgundy hover:bg-brand-burgundy hover:text-white hover:border-brand-burgundy"
        } transition-all`}
        data-testid={`tier-cta-${t.key}`}
      >
        <Link to={tierCtaHref(t.intent)}>{t.cta}</Link>
      </Button>
    </div>
  );
}
